import {
  DEFAULT_ALV_PERCENTAGE,
  deriveAdditionalWorkAlvPercentage,
  isAdditionalWorkRevenue,
} from './additionalWorkInvoicing';
import {
  calculateAdditionalWorkTotals,
  calculateAssignmentCosts,
  roundTo2Decimals,
  safeNumber,
} from './calculateValues';

/**
 * Pure calculations behind the four project budget boxes in ProjectView
 * (Urakka, Lisätyöt, Osakasmuutokset, Yhteensä) and the
 * Lisätyöt / Osakasmuutokset BudgetRows.
 *
 * Revenue rules:
 *  - Budjetoitu liikevaihto = Urakkasumma (project.contractSum) minus
 *    Hyvitykset (project.deductionsSum, entered as a positive amount).
 *  - Lisätyöt / Osakasmuutokset come from the ORDERED / CAN_BE_INVOICED /
 *    INVOICED items. The manual values in Projektin tiedot
 *    (project.additionalWorkSum / shareholderChangeSum) are only a fallback
 *    used when no such item exists.
 *
 * Every euro figure is tax-free and rounded to 2 decimals at the aggregate
 * level. Percentages are returned unrounded; the renderer formats them.
 *
 * The same rules are mirrored in the backend Excel budget report
 * (lixani-dynamodb-api: projectBudgetReport/utils/summarizeAdditionalWorksForBudgetReport.util.ts).
 */

/**
 * Kate percentage. Returns 0 (never NaN or Infinity) when revenue is 0.
 * @param {number} margin
 * @param {number} revenue
 * @returns {number}
 */
export const calculateMarginPercentage = (margin, revenue) => {
  const safeRevenue = safeNumber(revenue);
  return safeRevenue === 0 ? 0 : (safeNumber(margin) / safeRevenue) * 100;
};

/**
 * @param {number} revenue
 * @param {number} cost
 * @returns {{revenue: number, cost: number, margin: number, marginPercentage: number}}
 */
export const buildMarginSummary = (revenue, cost) => {
  const safeRevenue = roundTo2Decimals(safeNumber(revenue));
  const safeCost = roundTo2Decimals(safeNumber(cost));
  const margin = roundTo2Decimals(safeRevenue - safeCost);
  return {
    revenue: safeRevenue,
    cost: safeCost,
    margin,
    marginPercentage: calculateMarginPercentage(margin, safeRevenue),
  };
};

/**
 * Hyvitykset (credits granted to the client). Entered as a positive amount;
 * a negative value stored by the old dialog means the same deduction, so the
 * sign is ignored.
 * @param {{deductionsSum?: number|string}} [project]
 * @returns {number} always >= 0
 */
export const getDeductions = (project) =>
  roundTo2Decimals(Math.abs(safeNumber(project?.deductionsSum)));

/**
 * Budjetoitu liikevaihto = Urakkasumma (project.contractSum) − Hyvitykset.
 * project.budget (the retired Kokonaisbudjetti) is not read.
 * @param {{contractSum?: number|string, deductionsSum?: number|string}} [project]
 * @returns {number}
 */
export const getBudgetedRevenue = (project) =>
  roundTo2Decimals(safeNumber(project?.contractSum) - getDeductions(project));

/**
 * Items without a project field belong to every project (legacy rows).
 * Same rule the ProjectView budget rows have always used.
 */
export const belongsToProject = (item, projectId) =>
  !projectId || !item?.project || item.project === projectId;

/** Same alias fallback as selectCostsByAssignmentAndCategory. */
export const getAdditionalWorkRows = (item) =>
  item?.rows || item?.additionalWorkRows || item?.invoiceRows || [];

/**
 * productCredits (Tuotehyvitykset) are stored gross; convert them to a
 * tax-free amount with the item's own ALV (25.5 % fallback).
 * @returns {number}
 */
export const getProductCreditsTaxFree = (item) => {
  const gross = safeNumber(item?.productCredits);
  if (gross === 0) return 0;
  const alvPercentage = deriveAdditionalWorkAlvPercentage({
    rows: getAdditionalWorkRows(item),
  });
  return roundTo2Decimals(gross / (1 + alvPercentage / 100));
};

/**
 * Realized cost of one littera with the additional-work and
 * shareholder-change sale sums (booked into actualCost by the selector)
 * removed, i.e. urakka-only cost.
 */
export const getUrakkaActualCost = (entry) =>
  safeNumber(entry?.actualCost) -
  safeNumber(entry?.categories?.ADDITIONAL_WORK?.sum) -
  safeNumber(entry?.categories?.SHAREHOLDER_CHANGE?.sum);

/**
 * Realised cost side of a Lisätyöt / Osakasmuutokset section. Purchase
 * invoices cannot be linked to an item yet, so actualCost is always 0 for
 * now; the formulas already hold for a real value: whatever is not realised
 * is still ahead, and the expected final cost never drops below the realised.
 * @param {number} budgetedCost
 * @param {number} [actualCost=0]
 */
export const buildSectionCostForecast = (budgetedCost, actualCost = 0) => {
  const realised = roundTo2Decimals(safeNumber(actualCost));
  const incomingExpenses = roundTo2Decimals(
    Math.max(safeNumber(budgetedCost) - realised, 0)
  );
  return {
    actualCost: realised,
    incomingExpenses,
    expectedFinalCost: roundTo2Decimals(realised + incomingExpenses),
  };
};

/**
 * Liikevaihto / budjetoitu kustannus / budjetoitu kate for additional works
 * or shareholder changes. Only items with status ORDERED, CAN_BE_INVOICED or
 * INVOICED count.
 *  - revenue = Σ row taxFreeSum + Σ tax-free productCredits
 *  - cost    = the BUDGETED cost: Σ row taxFreeSum − Σ overhead (base price
 *              before the yleiskustannus markup). Realised costs would come
 *              from purchase invoices linked to the item, which do not exist
 *              yet, so nothing here is "toteutunut".
 * @param {Array<object>} items - selectAdditionalWorkItems / selectShareholderChangeItems
 * @param {string} [projectId]
 */
export const summarizeAdditionalWorkItems = (items, projectId) => {
  const acc = (items || []).reduce(
    (sum, item) => {
      if (!isAdditionalWorkRevenue(item)) return sum;
      if (!belongsToProject(item, projectId)) return sum;

      const totals = calculateAdditionalWorkTotals(
        getAdditionalWorkRows(item)
      );
      sum.itemCount += 1;
      sum.taxFreeSum += safeNumber(totals.taxFreeSum);
      sum.grossSum += safeNumber(totals.sum);
      sum.overhead += safeNumber(totals.overhead);
      sum.productCreditsGross += safeNumber(item.productCredits);
      sum.productCredits += getProductCreditsTaxFree(item);
      return sum;
    },
    {
      itemCount: 0,
      taxFreeSum: 0,
      grossSum: 0,
      overhead: 0,
      productCredits: 0,
      productCreditsGross: 0,
    }
  );

  const revenue = acc.taxFreeSum + acc.productCredits;
  const cost = acc.taxFreeSum - acc.overhead;
  const budgeted = buildMarginSummary(revenue, cost);

  return {
    itemCount: acc.itemCount,
    ...budgeted,
    ...buildSectionCostForecast(budgeted.cost),
    grossRevenue: roundTo2Decimals(acc.grossSum + acc.productCreditsGross),
    overhead: roundTo2Decimals(acc.overhead),
    productCredits: roundTo2Decimals(acc.productCredits),
  };
};

/**
 * Applies the manual Projektin tiedot value as a FALLBACK: it is used only
 * when no ORDERED / CAN_BE_INVOICED / INVOICED item exists. A manual value
 * carries no cost data, so cost = 0 and kate = liikevaihto.
 * @param {ReturnType<typeof summarizeAdditionalWorkItems>} section
 * @param {number|string} [manualSum] - project.additionalWorkSum / shareholderChangeSum
 * @returns {object} section with `source`: 'items' | 'manual' | 'none'
 */
export const applyManualFallback = (section, manualSum) => {
  if (section.itemCount > 0) return { ...section, source: 'items' };

  const manual = roundTo2Decimals(safeNumber(manualSum));
  if (manual === 0) return { ...section, source: 'none' };

  return {
    itemCount: 0,
    ...buildMarginSummary(manual, 0),
    ...buildSectionCostForecast(0),
    // Only used by the ALV-on Yhteensä cell of the BudgetRow.
    grossRevenue: roundTo2Decimals(manual * (1 + DEFAULT_ALV_PERCENTAGE / 100)),
    overhead: 0,
    productCredits: 0,
    source: 'manual',
  };
};

/** BudgetRow visibility: items exist or a non-zero manual fallback is in use. */
export const hasSectionData = (section) =>
  safeNumber(section?.itemCount) > 0 || section?.source === 'manual';

/**
 * Urakka (base contract) totals over the assignment entries of
 * invoice_assignment_data (each already run through countAssignmentValues).
 * Returns the BudgetRow summary keys, the expected final cost
 * (toteutunut + tulevat) and the Litterabudjetti Urakka-row keys
 * (originalBudget, updatedBudget, transfer, invoiceCost, kustannustoteuma,
 * completion) computed with the same rules as BudgetRow.applySummary.
 * @param {Array<object>} entries - Object.values(invoice_assignment_data)
 */
export const summarizeUrakkaAssignments = (entries) => {
  const acc = (entries || []).reduce(
    (sum, entry) => {
      const additionalWorkSum = safeNumber(
        entry?.categories?.ADDITIONAL_WORK?.sum
      );
      const shareholderChangeSum = safeNumber(
        entry?.categories?.SHAREHOLDER_CHANGE?.sum
      );
      const additionalWorkGross = safeNumber(
        entry?.categories?.ADDITIONAL_WORK?.totalSum
      );
      const shareholderChangeGross = safeNumber(
        entry?.categories?.SHAREHOLDER_CHANGE?.totalSum
      );
      const rowTotals = calculateAssignmentCosts(
        entry || {},
        entry?.ownInvoices || []
      );

      sum.finalBudget += safeNumber(entry?.finalBudget);
      sum.originalBudget += safeNumber(entry?.originalBudget?.value);
      sum.updatedBudget += safeNumber(entry?.updatedBudget?.value);
      sum.transfer += safeNumber(entry?.transfer?.value);
      sum.actualCost += getUrakkaActualCost(entry);
      // Selector value: already computed by the selector, never recomputed here.
      sum.incomingExpenses += safeNumber(entry?.incomingExpenses);
      sum.taxFreeTotal += Math.max(
        safeNumber(rowTotals.taxFreeSum) -
          additionalWorkSum -
          shareholderChangeSum,
        0
      );
      sum.addedTaxTotal += Math.max(
        safeNumber(rowTotals.sum) -
          additionalWorkGross -
          shareholderChangeGross,
        0
      );
      return sum;
    },
    {
      finalBudget: 0,
      originalBudget: 0,
      updatedBudget: 0,
      transfer: 0,
      actualCost: 0,
      incomingExpenses: 0,
      taxFreeTotal: 0,
      addedTaxTotal: 0,
    }
  );

  const finalBudget = roundTo2Decimals(acc.finalBudget);
  const actualCost = roundTo2Decimals(acc.actualCost);
  const incomingExpenses = roundTo2Decimals(acc.incomingExpenses);
  const expectedFinalCost = roundTo2Decimals(actualCost + incomingExpenses);

  return {
    finalBudget,
    actualCost,
    incomingExpenses,
    taxFreeTotal: roundTo2Decimals(acc.taxFreeTotal),
    addedTaxTotal: roundTo2Decimals(acc.addedTaxTotal),
    expectedFinalCost,
    originalBudget: roundTo2Decimals(acc.originalBudget),
    updatedBudget: roundTo2Decimals(acc.updatedBudget),
    transfer: roundTo2Decimals(acc.transfer),
    // Kirjanpito: realized cost without the transfers.
    invoiceCost: roundTo2Decimals(actualCost - acc.transfer),
    // Same rules as BudgetRow.applySummary.
    kustannustoteumaSum: finalBudget ? (actualCost / finalBudget) * 100 : 0,
    kustannustoteumaLeft:
      finalBudget > 0 ? roundTo2Decimals(finalBudget - actualCost) : 0,
    completion:
      finalBudget > 0 && expectedFinalCost
        ? ((expectedFinalCost - incomingExpenses) / expectedFinalCost) * 100
        : 0,
  };
};

/**
 * Single entry point for the four budget boxes and the Litterabudjetti
 * totals rows. `urakka` carries both the budgeted side (revenue = Urakkasumma
 * − Hyvitykset, cost = Σ finalBudget, margin) and the forecast side
 * (difference, marginEstimate) on top of summarizeUrakkaAssignments().
 * `total` carries the margin summary of Urakka + Lisätyöt + Osakasmuutokset
 * plus their realized (actualCost), still-ahead (incomingExpenses) and
 * expected final (expectedFinalCost) costs (see buildSectionCostForecast for
 * the Lisätyöt / Osakasmuutokset side).
 * @returns {{urakka, additionalWork, shareholderChange, total}}
 */
export const buildProjectBudgetSummary = ({
  project,
  entries,
  additionalWorkItems,
  shareholderChangeItems,
} = {}) => {
  const projectId = project?.id;

  const urakkaBase = summarizeUrakkaAssignments(entries);
  const budgeted = buildMarginSummary(
    getBudgetedRevenue(project),
    urakkaBase.finalBudget
  );
  const additionalWork = applyManualFallback(
    summarizeAdditionalWorkItems(additionalWorkItems, projectId),
    project?.additionalWorkSum
  );
  const shareholderChange = applyManualFallback(
    summarizeAdditionalWorkItems(shareholderChangeItems, projectId),
    project?.shareholderChangeSum
  );

  const marginEstimate = roundTo2Decimals(
    budgeted.revenue - urakkaBase.expectedFinalCost
  );
  const urakka = {
    ...urakkaBase,
    // Budjetoitu liikevaihto / kustannus / kate.
    ...budgeted,
    // Urakan erotus, positive = under budget.
    difference: roundTo2Decimals(
      budgeted.cost - urakkaBase.expectedFinalCost
    ),
    // Urakkakatearvio.
    marginEstimate,
    marginEstimatePercentage: calculateMarginPercentage(
      marginEstimate,
      budgeted.revenue
    ),
  };

  const total = {
    ...buildMarginSummary(
      urakka.revenue + additionalWork.revenue + shareholderChange.revenue,
      urakka.cost + additionalWork.cost + shareholderChange.cost
    ),
    actualCost: roundTo2Decimals(
      urakkaBase.actualCost +
        additionalWork.actualCost +
        shareholderChange.actualCost
    ),
    incomingExpenses: roundTo2Decimals(
      urakkaBase.incomingExpenses +
        additionalWork.incomingExpenses +
        shareholderChange.incomingExpenses
    ),
    expectedFinalCost: roundTo2Decimals(
      urakkaBase.expectedFinalCost +
        additionalWork.expectedFinalCost +
        shareholderChange.expectedFinalCost
    ),
  };

  return { urakka, additionalWork, shareholderChange, total };
};

/** BudgetRow `summary` prop for the Urakka row. */
export const toUrakkaBudgetRowSummary = ({ urakka }) => ({
  finalBudget: urakka.finalBudget,
  actualCost: urakka.actualCost,
  incomingExpenses: urakka.incomingExpenses,
  taxFreeTotal: urakka.taxFreeTotal,
  addedTaxTotal: urakka.addedTaxTotal,
});

/**
 * BudgetRow `summary` prop for the Lisätyöt / Osakasmuutokset rows.
 * Budjetoitu = the budgeted cost of the ordered rows, Toteutunut = the
 * section's realised cost (0 until purchase invoices can be linked to an
 * item), Tulevat = what is still ahead (buildSectionCostForecast).
 * Yhteensä = revenue (tax-free or gross by showAlv).
 */
export const toAdditionalWorkBudgetRowSummary = (section) => ({
  finalBudget: section.cost,
  actualCost: section.actualCost,
  incomingExpenses: section.incomingExpenses,
  taxFreeTotal: section.revenue,
  addedTaxTotal: section.grossRevenue,
});
